import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CubeIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  MegaphoneIcon,
  CogIcon,
  InformationCircleIcon,
  ClockIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookiePolicyPage: React.FC = () => {
  const lastUpdated = "January 21, 2026";
  const effectiveDate = "January 21, 2026";

  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always required
    functional: true,
    analytics: false,
    marketing: false,
  });

  const handlePreferenceChange = (category: keyof CookiePreferences) => {
    if (category === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const savePreferences = () => {
    // In production, this would save to localStorage and update consent management
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    alert('Cookie preferences saved successfully!');
  };

  const sections = [
    { id: 'what-are-cookies', title: '1. What Are Cookies?' },
    { id: 'how-we-use', title: '2. How We Use Cookies' },
    { id: 'types', title: '3. Types of Cookies' },
    { id: 'cookie-list', title: '4. Cookies We Use' },
    { id: 'third-party', title: '5. Third-Party Cookies' },
    { id: 'manage', title: '6. Managing Cookies' },
    { id: 'preferences', title: '7. Your Preferences' },
    { id: 'changes', title: '8. Policy Changes' },
    { id: 'contact', title: '9. Contact Us' },
  ];

  const cookieCategories = [
    {
      name: 'Necessary',
      key: 'necessary' as keyof CookiePreferences,
      icon: ShieldCheckIcon,
      color: 'green',
      required: true,
      description: 'Essential for the website to function properly. These cookies cannot be disabled.',
      examples: ['Session management', 'Security tokens', 'CSRF protection', 'Load balancing']
    },
    {
      name: 'Functional',
      key: 'functional' as keyof CookiePreferences,
      icon: CogIcon,
      color: 'blue',
      required: false,
      description: 'Enable personalized features and remember your preferences.',
      examples: ['Language preferences', 'Theme settings', 'Wallet connection state', 'UI customizations']
    },
    {
      name: 'Analytics',
      key: 'analytics' as keyof CookiePreferences,
      icon: ChartBarIcon,
      color: 'purple',
      required: false,
      description: 'Help us understand how visitors interact with our platform to improve user experience.',
      examples: ['Page views', 'Navigation patterns', 'Feature usage', 'Error tracking']
    },
    {
      name: 'Marketing',
      key: 'marketing' as keyof CookiePreferences,
      icon: MegaphoneIcon,
      color: 'orange',
      required: false,
      description: 'Used to deliver relevant advertisements and track campaign effectiveness.',
      examples: ['Ad personalization', 'Campaign tracking', 'Remarketing', 'Social media pixels']
    }
  ];

  const detailedCookies = [
    {
      category: 'Necessary',
      cookies: [
        { name: 'session_id', purpose: 'Maintains user session', duration: 'Session', provider: 'TEMPLUM' },
        { name: 'csrf_token', purpose: 'Security - prevents cross-site request forgery', duration: 'Session', provider: 'TEMPLUM' },
        { name: '__cf_bm', purpose: 'Cloudflare bot management', duration: '30 minutes', provider: 'Cloudflare' },
        { name: 'cookie_consent', purpose: 'Stores your cookie preferences', duration: '1 year', provider: 'TEMPLUM' },
      ]
    },
    {
      category: 'Functional',
      cookies: [
        { name: 'theme', purpose: 'Remembers dark/light mode preference', duration: '1 year', provider: 'TEMPLUM' },
        { name: 'language', purpose: 'Stores language preference', duration: '1 year', provider: 'TEMPLUM' },
        { name: 'wallet_connected', purpose: 'Remembers wallet connection state', duration: '7 days', provider: 'TEMPLUM' },
        { name: 'sidebar_state', purpose: 'Remembers sidebar collapsed/expanded state', duration: '1 year', provider: 'TEMPLUM' },
      ]
    },
    {
      category: 'Analytics',
      cookies: [
        { name: '_ga', purpose: 'Google Analytics - distinguishes users', duration: '2 years', provider: 'Google' },
        { name: '_gid', purpose: 'Google Analytics - distinguishes users', duration: '24 hours', provider: 'Google' },
        { name: '_gat', purpose: 'Google Analytics - throttles request rate', duration: '1 minute', provider: 'Google' },
        { name: 'mp_*', purpose: 'Mixpanel - product analytics', duration: '1 year', provider: 'Mixpanel' },
      ]
    },
    {
      category: 'Marketing',
      cookies: [
        { name: '_fbp', purpose: 'Facebook Pixel - ad measurement', duration: '3 months', provider: 'Meta' },
        { name: '_gcl_au', purpose: 'Google Ads conversion tracking', duration: '3 months', provider: 'Google' },
        { name: 'twitter_pixel', purpose: 'Twitter ad tracking', duration: '2 years', provider: 'Twitter' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-dark-900 to-dark-950">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <CubeIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Cookie Policy
            </h1>
            <p className="text-xl text-gray-400 mb-6">
              This policy explains how TEMPLUM uses cookies and similar technologies to recognize you when you visit our platform.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4" />
                Last Updated: {lastUpdated}
              </span>
              <span className="flex items-center gap-2">
                <DocumentTextIcon className="w-4 h-4" />
                Effective: {effectiveDate}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Navigation</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-gray-400 hover:text-primary-400 transition-colors py-1"
                >
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Categories Overview */}
      <section className="py-12 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Cookie Categories</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cookieCategories.map((category) => {
                const Icon = category.icon;
                const colorClasses = {
                  green: 'text-green-400 bg-green-500/20 border-green-500/30',
                  blue: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
                  purple: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
                  orange: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
                };
                return (
                  <div
                    key={category.key}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-dark-600 transition-colors"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colorClasses[category.color as keyof typeof colorClasses]}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{category.name}</h3>
                    <p className="text-sm text-gray-400 mb-4">{category.description}</p>
                    {category.required && (
                      <span className="inline-block px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                        Always Active
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-invert prose-lg max-w-none">

              {/* Section 1 */}
              <section id="what-are-cookies" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">1</span>
                  What Are Cookies?
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to the owners of the site.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mt-6">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">First-Party Cookies</h4>
                      <p className="text-gray-400 text-sm">Set by the website you are visiting (TEMPLUM). They can only be read by us.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Third-Party Cookies</h4>
                      <p className="text-gray-400 text-sm">Set by other companies (like analytics providers) and can be read by them on multiple sites.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Session Cookies</h4>
                      <p className="text-gray-400 text-sm">Temporary cookies that expire when you close your browser.</p>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Persistent Cookies</h4>
                      <p className="text-gray-400 text-sm">Remain on your device until they expire or you delete them.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 2 */}
              <section id="how-we-use" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">2</span>
                  How We Use Cookies
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">We use cookies and similar technologies for several purposes:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary-400 rounded-full mt-2"></span>
                      <div>
                        <strong className="text-white">Authentication:</strong>
                        <span className="text-gray-400"> To recognize you when you sign in and keep you logged in</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary-400 rounded-full mt-2"></span>
                      <div>
                        <strong className="text-white">Security:</strong>
                        <span className="text-gray-400"> To protect your account and prevent fraud</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary-400 rounded-full mt-2"></span>
                      <div>
                        <strong className="text-white">Preferences:</strong>
                        <span className="text-gray-400"> To remember your settings and customizations</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary-400 rounded-full mt-2"></span>
                      <div>
                        <strong className="text-white">Analytics:</strong>
                        <span className="text-gray-400"> To understand how you use our platform and improve it</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary-400 rounded-full mt-2"></span>
                      <div>
                        <strong className="text-white">Marketing:</strong>
                        <span className="text-gray-400"> To show you relevant advertisements (with your consent)</span>
                      </div>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Section 3 */}
              <section id="types" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">3</span>
                  Types of Cookies We Use
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 space-y-6">
                  {cookieCategories.map((category) => {
                    const Icon = category.icon;
                    return (
                      <div key={category.key} className="p-4 bg-dark-900/50 rounded-lg">
                        <div className="flex items-center gap-3 mb-3">
                          <Icon className={`w-6 h-6 ${
                            category.color === 'green' ? 'text-green-400' :
                            category.color === 'blue' ? 'text-blue-400' :
                            category.color === 'purple' ? 'text-purple-400' :
                            'text-orange-400'
                          }`} />
                          <h4 className="text-white font-semibold">{category.name} Cookies</h4>
                          {category.required && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Required</span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mb-3">{category.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {category.examples.map((example, idx) => (
                            <span key={idx} className="px-2 py-1 bg-dark-800 text-gray-400 text-xs rounded">
                              {example}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 4 - Cookie List */}
              <section id="cookie-list" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">4</span>
                  Cookies We Use
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-6">
                    Below is a detailed list of the cookies we use on TEMPLUM:
                  </p>
                  {detailedCookies.map((group) => (
                    <div key={group.category} className="mb-6 last:mb-0">
                      <h4 className="text-lg font-semibold text-white mb-4">{group.category} Cookies</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-dark-600">
                              <th className="text-left py-3 px-4 text-white">Cookie Name</th>
                              <th className="text-left py-3 px-4 text-white">Purpose</th>
                              <th className="text-left py-3 px-4 text-white">Duration</th>
                              <th className="text-left py-3 px-4 text-white">Provider</th>
                            </tr>
                          </thead>
                          <tbody className="text-gray-400">
                            {group.cookies.map((cookie, idx) => (
                              <tr key={idx} className="border-b border-dark-700">
                                <td className="py-3 px-4 font-mono text-primary-400">{cookie.name}</td>
                                <td className="py-3 px-4">{cookie.purpose}</td>
                                <td className="py-3 px-4">{cookie.duration}</td>
                                <td className="py-3 px-4">{cookie.provider}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 5 */}
              <section id="third-party" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">5</span>
                  Third-Party Cookies
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    We use services from third parties that may set their own cookies on your device. These include:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Google Analytics</h4>
                      <p className="text-gray-400 text-sm mb-2">Web analytics service that tracks and reports website traffic.</p>
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-400 text-sm hover:underline">
                        Google Privacy Policy →
                      </a>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Cloudflare</h4>
                      <p className="text-gray-400 text-sm mb-2">Security and performance services including DDoS protection.</p>
                      <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-primary-400 text-sm hover:underline">
                        Cloudflare Privacy Policy →
                      </a>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Meta (Facebook)</h4>
                      <p className="text-gray-400 text-sm mb-2">Advertising and social media integration.</p>
                      <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer" className="text-primary-400 text-sm hover:underline">
                        Meta Privacy Policy →
                      </a>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Mixpanel</h4>
                      <p className="text-gray-400 text-sm mb-2">Product analytics to improve user experience.</p>
                      <a href="https://mixpanel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary-400 text-sm hover:underline">
                        Mixpanel Privacy Policy →
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 6 */}
              <section id="manage" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">6</span>
                  Managing Cookies
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-6">
                    You can control and manage cookies in several ways:
                  </p>
                  <div className="space-y-4">
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Browser Settings</h4>
                      <p className="text-gray-400 text-sm mb-3">
                        Most browsers allow you to control cookies through their settings. You can:
                      </p>
                      <ul className="text-gray-400 text-sm space-y-1 ml-4">
                        <li>• View what cookies are stored and delete them individually</li>
                        <li>• Block third-party cookies</li>
                        <li>• Block all cookies from certain sites</li>
                        <li>• Clear all cookies when you close the browser</li>
                      </ul>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary-400 text-sm hover:underline">Chrome</a>
                        <span className="text-gray-600">|</span>
                        <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-primary-400 text-sm hover:underline">Firefox</a>
                        <span className="text-gray-600">|</span>
                        <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary-400 text-sm hover:underline">Safari</a>
                        <span className="text-gray-600">|</span>
                        <a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-primary-400 text-sm hover:underline">Edge</a>
                      </div>
                    </div>
                    <div className="p-4 bg-dark-900/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Opt-Out Tools</h4>
                      <p className="text-gray-400 text-sm mb-3">
                        You can opt out of certain third-party cookies:
                      </p>
                      <ul className="text-gray-400 text-sm space-y-1 ml-4">
                        <li>• <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">Google Analytics Opt-out</a></li>
                        <li>• <a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">Your Online Choices (EU)</a></li>
                        <li>• <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">Network Advertising Initiative</a></li>
                      </ul>
                    </div>
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <div className="flex items-start gap-3">
                        <InformationCircleIcon className="w-5 h-5 text-yellow-400 mt-0.5" />
                        <p className="text-yellow-200 text-sm">
                          <strong>Note:</strong> Disabling certain cookies may affect the functionality of TEMPLUM. You may not be able to use all features if you block necessary cookies.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 7 - Preferences */}
              <section id="preferences" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">7</span>
                  Your Cookie Preferences
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-6">
                    Manage your cookie preferences below. Changes will take effect immediately.
                  </p>
                  <div className="space-y-4">
                    {cookieCategories.map((category) => {
                      const Icon = category.icon;
                      return (
                        <div
                          key={category.key}
                          className="flex items-center justify-between p-4 bg-dark-900/50 rounded-lg"
                        >
                          <div className="flex items-center gap-4">
                            <Icon className={`w-6 h-6 ${
                              category.color === 'green' ? 'text-green-400' :
                              category.color === 'blue' ? 'text-blue-400' :
                              category.color === 'purple' ? 'text-purple-400' :
                              'text-orange-400'
                            }`} />
                            <div>
                              <h4 className="text-white font-semibold">{category.name}</h4>
                              <p className="text-gray-400 text-sm">{category.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {category.required ? (
                              <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded">
                                Always Active
                              </span>
                            ) : (
                              <button
                                onClick={() => handlePreferenceChange(category.key)}
                                className={`relative w-12 h-6 rounded-full transition-colors ${
                                  preferences[category.key]
                                    ? 'bg-primary-500'
                                    : 'bg-dark-600'
                                }`}
                              >
                                <span
                                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                                    preferences[category.key]
                                      ? 'translate-x-7'
                                      : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-6 flex justify-end gap-4">
                    <button
                      onClick={() => setPreferences({
                        necessary: true,
                        functional: false,
                        analytics: false,
                        marketing: false,
                      })}
                      className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                    >
                      Reject All Non-Essential
                    </button>
                    <button
                      onClick={() => setPreferences({
                        necessary: true,
                        functional: true,
                        analytics: true,
                        marketing: true,
                      })}
                      className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={savePreferences}
                      className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              </section>

              {/* Section 8 */}
              <section id="changes" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">8</span>
                  Policy Changes
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    We may update this Cookie Policy from time to time to reflect changes in the cookies we use or for other operational, legal, or regulatory reasons. When we make changes:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li>We will update the "Last Updated" date at the top of this policy</li>
                    <li>We will display a notice on our website to inform you of material changes</li>
                    <li>We may ask you to renew your consent for non-essential cookies</li>
                  </ul>
                  <p className="text-gray-300 mt-4">
                    Please check this page periodically for updates.
                  </p>
                </div>
              </section>

              {/* Section 9 */}
              <section id="contact" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center text-primary-400 text-sm">9</span>
                  Contact Us
                </h2>
                <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <p className="text-gray-300 mb-4">
                    If you have questions or concerns about this Cookie Policy or our use of cookies, please contact us:
                  </p>
                  <div className="p-4 bg-dark-900/50 rounded-lg">
                    <p className="text-white font-semibold">NARDIHA Holdings S.L.</p>
                    <p className="text-gray-400">Email: privacy@templum.io</p>
                    <p className="text-gray-400">Data Protection Officer: dpo@nardiha.com</p>
                  </div>
                </div>
              </section>

            </div>

            {/* Related Documents */}
            <div className="mt-12 p-6 bg-dark-800/50 rounded-xl border border-dark-700">
              <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/legal/terms"
                  className="px-4 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/legal/privacy"
                  className="px-4 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>

            {/* Back to top */}
            <div className="mt-8 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-primary-400 hover:text-primary-300 text-sm"
              >
                ↑ Back to top
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicyPage;
